import { useState, type JSX } from 'react';
import useMonduHandoffSkip from '@/enterprise/data/useMonduHandoffSkip';
import AccelerateAdditionalReceivables from '@/enterprise/features/extendedNetworkFinance/AccelerateAdditionalReceivables';
import ContinueToMondu from '@/enterprise/features/extendedNetworkFinance/ContinueToMondu';
import EligibilityCriteria from '@/enterprise/features/extendedNetworkFinance/EligibilityCriteria';
import Faqs from '@/enterprise/features/extendedNetworkFinance/Faqs';
import Header from '@/enterprise/features/extendedNetworkFinance/Header';
import HowItWorks from '@/enterprise/features/extendedNetworkFinance/HowItWorks';
import MonduHandoffModal from '@/enterprise/features/extendedNetworkFinance/MonduHandoffModal';
import PricingPreview from '@/enterprise/features/extendedNetworkFinance/PricingPreview';
import useMonduHandoffRedirect, {
  type ConsentPath,
} from '@/enterprise/features/extendedNetworkFinance/useMonduHandoffRedirect';
import { useReporting } from '@/reporting';

type CtaTrigger = 'header' | 'continue-section';

/**
 * Extended Network Finance marketing page.
 * Feature-flag gated; content sections follow the approved marketing-page order.
 */
const ExtendedNetworkFinance = (): JSX.Element => {
  const [monduHandoffOpen, setMonduHandoffOpen] = useState(false);
  // Captured when the CTA is pressed, not re-read from `canSkipModal`, so the modal reports the same
  // standing the handoff it follows was started with even if the skip queries settle in between.
  const [handoffConsentPath, setHandoffConsentPath] = useState<ConsentPath>('modal');
  const [pendingCta, setPendingCta] = useState<CtaTrigger | null>(null);
  const { canSkipModal } = useMonduHandoffSkip();
  const { runHandoff, isBusy, hasError, clearError } = useMonduHandoffRedirect();
  const { trackEnterpriseEvent } = useReporting();

  /**
   * A supplier who already acknowledged the current Mondu consent and already has a Mondu supplier
   * has nothing left to disclose, so the CTA runs the handoff directly. Everyone else — including
   * every supplier whose consent state is still loading or failed to load — gets the modal.
   */
  const handleCtaClick = async (trigger: CtaTrigger, ctaLabel: string): Promise<void> => {
    // Reported here rather than in the CTA components so the recorded consent path and the branch
    // taken below are read from the same value. `canSkipModal` fails closed, so a supplier whose
    // consent state is pending or errored reports `modal` and gets the modal.
    const consentPath = canSkipModal ? 'prior-consent' : 'modal';

    void trackEnterpriseEvent('extended-network-marketing::cta::clicked', { trigger, ctaLabel, consentPath });

    if (!canSkipModal) {
      setHandoffConsentPath('modal');
      setMonduHandoffOpen(true);
      return;
    }

    setPendingCta(trigger);

    // On success the browser is already leaving; keep the CTA spinning rather than flashing it back
    // to its idle state mid-navigation.
    // The modal this failure opens is a retry for a supplier who already consented, so it keeps
    // reporting `prior-consent` rather than claiming a fresh consent decision.
    if (!(await runHandoff({ consentPath: 'prior-consent' }))) {
      setPendingCta(null);
      setHandoffConsentPath('prior-consent');
      setMonduHandoffOpen(true);
    }
  };

  const closeMonduHandoff = (): void => {
    setMonduHandoffOpen(false);
    clearError();
  };

  return (
    <div className="mx-auto max-w-full space-y-20 md:max-w-7xl">
      <Header
        onCtaClick={(ctaLabel) => void handleCtaClick('header', ctaLabel)}
        ctaLoading={pendingCta === 'header'}
        ctaDisabled={isBusy}
      />
      <AccelerateAdditionalReceivables />
      <EligibilityCriteria />
      <HowItWorks />
      <PricingPreview />
      <ContinueToMondu
        onCtaClick={(ctaLabel) => void handleCtaClick('continue-section', ctaLabel)}
        ctaLoading={pendingCta === 'continue-section'}
        ctaDisabled={isBusy}
      />
      <Faqs />
      <MonduHandoffModal
        open={monduHandoffOpen}
        onClose={closeMonduHandoff}
        consentPath={handoffConsentPath}
        showRequestError={hasError}
      />
    </div>
  );
};

export default ExtendedNetworkFinance;
